import {
  createBaseThemeOptions,
  createUnifiedTheme,
  genPageTheme,
  palettes,
  shapes,
} from '@backstage/theme';
import { edekaColors, edekaFontFamilies } from './edekaColors';

const pageHeader = genPageTheme({
  colors: [edekaColors.blueActive, edekaColors.blue],
  shape: shapes.wave,
});

const pageHeaderAccent = genPageTheme({
  colors: [edekaColors.blue, edekaColors.yellow],
  shape: shapes.wave2,
});

/**
 * Light EDEKA theme for Backstage (Pokemon / B2C Pages developer portal).
 * Colors and fonts follow the mobile/RSC design system tokens.
 */
export const edekaTheme = createUnifiedTheme({
  ...createBaseThemeOptions({
    palette: {
      ...palettes.light,
      primary: {
        main: edekaColors.blueActive,
        light: '#4A9AE0',
        dark: edekaColors.blue,
        contrastText: edekaColors.white,
      },
      secondary: {
        main: edekaColors.yellow,
        light: '#FDF06A',
        dark: '#E0C91A',
        contrastText: edekaColors.textPrimary,
      },
      error: {
        main: edekaColors.error,
      },
      warning: {
        main: edekaColors.warning,
      },
      success: {
        main: edekaColors.success,
      },
      info: {
        main: edekaColors.blue,
      },
      background: {
        default: edekaColors.greyWhite,
        paper: edekaColors.white,
      },
      text: {
        primary: edekaColors.textPrimary,
        secondary: edekaColors.textSecondary,
      },
      banner: {
        info: edekaColors.blueActive,
        error: edekaColors.error,
        text: edekaColors.white,
        link: edekaColors.yellow,
      },
      errorBackground: '#FDF2F3',
      warningBackground: '#FFF6E5',
      infoBackground: edekaColors.paybackBg,
      navigation: {
        background: edekaColors.blue,
        indicator: edekaColors.yellow,
        color: '#D6E8F8',
        selectedColor: edekaColors.white,
        navItem: {
          hoverBackground: '#155A9E',
        },
        submenu: {
          background: '#144E8A',
        },
      },
    },
    fontFamily: edekaFontFamilies.text,
    defaultPageTheme: 'home',
    pageTheme: {
      home: pageHeader,
      documentation: pageHeaderAccent,
      tool: pageHeader,
      service: pageHeader,
      website: pageHeader,
      library: pageHeader,
      other: pageHeader,
      app: pageHeader,
      apis: pageHeaderAccent,
    },
    typography: {
      htmlFontSize: 16,
      fontFamily: edekaFontFamilies.text,
      h1: {
        fontFamily: edekaFontFamilies.headline,
        fontSize: 54,
        fontWeight: 900,
        marginBottom: 16,
      },
      h2: {
        fontFamily: edekaFontFamilies.headline,
        fontSize: 40,
        fontWeight: 900,
        marginBottom: 14,
      },
      h3: {
        fontFamily: edekaFontFamilies.headline,
        fontSize: 32,
        fontWeight: 900,
        marginBottom: 12,
      },
      h4: {
        fontFamily: edekaFontFamilies.textBold,
        fontSize: 28,
        fontWeight: 700,
        marginBottom: 10,
      },
      h5: {
        fontFamily: edekaFontFamilies.textBold,
        fontSize: 22,
        fontWeight: 700,
        marginBottom: 8,
      },
      h6: {
        fontFamily: edekaFontFamilies.textBold,
        fontSize: 18,
        fontWeight: 700,
        marginBottom: 6,
      },
    },
  }),
  components: {
    MuiButton: {
      styleOverrides: {
        containedPrimary: {
          fontFamily: edekaFontFamilies.textBold,
          textTransform: 'none' as const,
          borderRadius: 4,
        },
        containedSecondary: {
          fontFamily: edekaFontFamilies.textBold,
          color: edekaColors.textPrimary,
          textTransform: 'none' as const,
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFamily: edekaFontFamilies.text,
        },
      },
    },
  },
});
